import QRCode from 'qrcode';
import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { baht, main } from '../lib/text';
import type { Bill } from '../lib/types';
import { handleAuthError } from './useStaff';

const METHODS = [['cash', 'เงินสด · Cash'], ['promptpay', 'พร้อมเพย์ · PromptPay'], ['card', 'บัตร · Card']] as const;
const STATUS: Record<string, string> = { queued: 'รอคิว', cooking: 'กำลังทำ', ready: 'รอเสิร์ฟ' };

/**
 * Checkout for a table or a takeout: unfinished dishes first (serve or cancel with a reason), then the bill with
 * the guest's tip and the restaurant's PromptPay QR, then "paid" with the method. Paying closes the sitting.
 */
export function Checkout({ token, sessionId, title, onDone, onClose }: { token: string; sessionId: string; title: string; onDone: () => void; onClose: () => void }) {
  const [bill, setBill] = useState<Bill | null>(null);
  const [qr, setQr] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const b = await api<Bill>('GET', `/sessions/${sessionId}/bill`, { token });
      setBill(b);
      setQr(b.promptpay ? await QRCode.toString(b.promptpay.payload, { type: 'svg', margin: 1 }) : '');
    } catch (e) { handleAuthError(e); setErr((e as Error).message); }
  }, [token, sessionId]);
  useEffect(() => { void load(); }, [load]);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setErr(null);
    try { await fn(); await load(); } catch (e) { handleAuthError(e); setErr((e as ApiError).message); await load(); } finally { setBusy(false); }
  };
  const line = (u: Bill['unresolved'][number], status: 'served' | 'cancelled', reason?: string) =>
    api('PUT', '/kitchen/lines', { token, body: { status, ...(reason ? { reason } : {}), lines: [{ sessionId: u.sessionId, orderId: u.orderId, lineId: u.lineId }] } });

  const pay = (method: string) => act(async () => {
    await api('POST', `/sessions/${sessionId}/pay`, { token, body: { method, expectTotalSatang: bill!.totalSatang } });
    onDone();
  });

  return (
    <div className="sheet-wrap" onClick={onClose}>
      <section className="sheet" role="dialog" aria-modal="true" aria-label={`Checkout ${title}`} onClick={(e) => e.stopPropagation()} style={{ color: 'var(--ink)' }}>
        <div className="row">
          <h2 className="display" style={{ margin: 0, fontSize: 24 }}>เช็คบิล · {title}</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Close">×</button>
        </div>
        {!bill ? <div className="spinner" role="status" /> : (
          <>
            {bill.unresolved.length > 0 && (
              <section className="warn" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <b>ยังไม่เสิร์ฟ {bill.unresolved.length} รายการ · Not served yet: serve or cancel before payment</b>
                {bill.unresolved.map((u) => (
                  <div key={u.orderId + u.lineId} className="row" style={{ gap: 6 }}>
                    <span>{u.qty}× {main(u.name)} <span className="muted">· {STATUS[u.status]}</span></span>
                    <span style={{ display: 'flex', gap: 6 }}>
                      <button className="dish-step ready" disabled={busy} onClick={() => void act(() => line(u, 'served'))}>เสิร์ฟ</button>
                      <button className="dish-step" style={{ background: '#FFE4E5' }} disabled={busy} onClick={() => {
                        const reason = prompt('เหตุผลที่ยกเลิก · Reason for cancelling', 'ลูกค้ายกเลิก');
                        if (reason !== null) void act(() => line(u, 'cancelled', reason || 'cancelled'));
                      }}>ยกเลิก</button>
                    </span>
                  </div>
                ))}
              </section>
            )}

            <ul className="ticket" style={{ listStyle: 'none', margin: 0, gap: 6 }}>
              {bill.lines.map((l) => (
                <li key={l.orderId + l.lineId} className="row" style={{ textDecoration: l.status === 'cancelled' ? 'line-through' : undefined, color: l.status === 'cancelled' ? 'var(--ink-2)' : undefined }}>
                  <span>{l.qty}× {main(l.name)}{l.choices.length ? <span className="muted" style={{ fontSize: 12 }}> · {l.choices.map(main).join(', ')}</span> : null}
                    {l.cancelReason && <span className="muted" style={{ fontSize: 12 }}> ({l.cancelReason})</span>}</span>
                  <span>{baht(l.unitSatang * l.qty)}</span>
                </li>
              ))}
              <li className="row" style={{ borderTop: '2px dashed var(--ink)', paddingTop: 6 }}><span>รวม · Subtotal</span><span>{baht(bill.subtotalSatang)}</span></li>
              <li className="row"><span>ทิป · Tip (guest)</span><span>{baht(bill.tipSatang)}</span></li>
              <li className="row display" style={{ fontSize: 22 }}><span>ยอดชำระ · Total</span><span>{baht(bill.totalSatang)}</span></li>
            </ul>

            {qr ? (
              <div className="ticket" style={{ alignItems: 'center' }}>
                <div className="qr" style={{ border: '3px solid var(--ink)' }} role="img" aria-label="PromptPay QR" dangerouslySetInnerHTML={{ __html: qr }} />
                <span className="muted" style={{ fontSize: 13 }}>พร้อมเพย์ {baht(bill.totalSatang)} · scan with any Thai banking app</span>
              </div>
            ) : <span className="muted" style={{ fontSize: 13 }}>ตั้งค่าพร้อมเพย์ใน Settings เพื่อแสดง QR · Add your PromptPay ID in Settings to show a QR here.</span>}

            {err && <div className="warn" role="alert">{err}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
              {METHODS.map(([m, label]) => (
                <button key={m} className="cta light" style={{ fontSize: 15, padding: 10 }} disabled={busy || bill.unresolved.length > 0} onClick={() => void pay(m)}>{label}</button>
              ))}
            </div>
            <span className="muted" style={{ fontSize: 12, textAlign: 'center' }}>กดเมื่อได้รับเงินแล้ว โต๊ะจะปิดอัตโนมัติ · Tap when paid; the sitting closes.</span>
          </>
        )}
      </section>
    </div>
  );
}
